import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Warning, Question } from '@phosphor-icons/react';

// Simple module-level pub/sub so we can call confirmDialog() from anywhere
let _resolver = null;
let _setState = null;

/**
 * Opens the global confirm dialog. Returns a Promise<boolean>.
 * Usage:
 *   import { confirmDialog } from '@/components/ConfirmDialog';
 *   if (!(await confirmDialog({ title: '...', description: '...' }))) return;
 *
 * Also accepts a plain string as the description (backward-compat with window.confirm):
 *   await confirmDialog('Obrisati zapis?')
 */
export function confirmDialog(options) {
  const opts = typeof options === 'string' ? { description: options } : (options || {});
  return new Promise((resolve) => {
    _resolver = resolve;
    if (_setState) {
      _setState({
        open: true,
        title: opts.title || 'Potvrda',
        description: opts.description || '',
        confirmText: opts.confirmText || 'Potvrdi',
        cancelText: opts.cancelText || 'Odustani',
        variant: opts.variant || 'default', // 'default' | 'danger'
      });
    } else {
      resolve(false);
    }
  });
}

const DEFAULT_STATE = {
  open: false,
  title: 'Potvrda',
  description: '',
  confirmText: 'Potvrdi',
  cancelText: 'Odustani',
  variant: 'default',
};

export default function ConfirmDialog() {
  const [state, setState] = useState(DEFAULT_STATE);

  useEffect(() => {
    _setState = setState;
    return () => { _setState = null; };
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (_resolver) {
      const r = _resolver;
      _resolver = null;
      r(result);
    }
  };

  const isDanger = state.variant === 'danger';
  const Icon = isDanger ? Warning : Question;
  const iconBg = isDanger ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600';
  const confirmCls = isDanger
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-blue-600 hover:bg-blue-700 text-white';

  return (
    <Dialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
      <DialogContent className="max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`rounded-full p-2.5 ${iconBg} flex-shrink-0`}>
              <Icon size={22} weight="bold" />
            </div>
            <div className="flex-1 pt-0.5">
              <DialogTitle className="text-base font-semibold">{state.title}</DialogTitle>
              {state.description && (
                <DialogDescription className="mt-1.5 text-sm text-slate-600 whitespace-pre-wrap">
                  {state.description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2 mt-2">
          <Button variant="outline" onClick={() => close(false)} data-testid="confirm-cancel-btn">
            {state.cancelText}
          </Button>
          <Button onClick={() => close(true)} className={confirmCls} data-testid="confirm-ok-btn">
            {state.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
